import { FC, useMemo, useState } from 'react';
import { Checkbox, Input } from 'antd';
import { Tags as TagsIcon } from 'lucide-react';
import { TbSearch } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useTags } from '@/queries';
import { EmptySection } from '@/shared';
import { BaseSkeleton } from '@/shared/components/skeletons';

interface TagsFilterProps {
    filters: Record<string, unknown>;
    handleChange: (path: string[], value: string[]) => void;
}

const TagsFilter: FC<TagsFilterProps> = ({ filters, handleChange }) => {
    const { token } = useAntDesignTheme();
    const { data: tags = [], isLoading } = useTags();
    const [search, setSearch] = useState('');
    const filteredTags = useMemo(
        () => tags.filter((tag) => tag.name.toLowerCase().includes(search.trim().toLowerCase())),
        [search, tags],
    );

    if (isLoading) {
        return <BaseSkeleton active paragraph={{ rows: 6 }} title={false} />;
    }

    return (
        <>
            <Input
                placeholder='Search Tags...'
                prefix={<TbSearch style={{ color: token.colorTextQuaternary }} />}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
            />
            {filteredTags.length ? (
                <Checkbox.Group
                    value={Array.isArray(filters.workspaceTags) ? filters.workspaceTags : []}
                    style={{ marginTop: '0.75rem', width: '100%' }}
                    onChange={(values) => handleChange(['workspaceTags'], values.map(String))}
                >
                    {filteredTags.map((tag) => (
                        <div
                            key={tag.id}
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                gap: '0.5rem',
                                marginBottom: '0.375rem',
                                minWidth: 0,
                                width: '100%',
                            }}
                        >
                            <Checkbox id={`search-tag-${tag.id}`} value={tag.id} />
                            <label
                                htmlFor={`search-tag-${tag.id}`}
                                style={{
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    flex: 1,
                                    gap: '0.375rem',
                                    minWidth: 0,
                                }}
                            >
                                <span
                                    style={{
                                        backgroundColor: tag.color ?? token.colorTextTertiary,
                                        borderRadius: '50%',
                                        flex: '0 0 auto',
                                        height: '0.5rem',
                                        width: '0.5rem',
                                    }}
                                />
                                <span
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {tag.name}
                                </span>
                                <small style={{ color: token.colorTextTertiary, flex: '0 0 auto' }}>
                                    {tag.assignmentCount}
                                </small>
                            </label>
                        </div>
                    ))}
                </Checkbox.Group>
            ) : (
                <EmptySection
                    description={
                        search ? 'No tags match your search.' : 'Create tags in Preferences to filter by them.'
                    }
                    height='10rem'
                    icon={<TagsIcon size='1.25rem' style={{ color: token.colorTextTertiary }} />}
                    label={search ? 'No Tags Found' : 'No Tags Yet'}
                />
            )}
        </>
    );
};

export default TagsFilter;
